import { confirmMes } from '@/shared/utilities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import type { ConversationRole } from '../models/member';
import type { PermissionOverrideItemDto } from '../models/permission';
import { assignMemberRole, transferOwnership, updateMemberOverrides } from '../services/permission.service';
import { conversationKeys } from './conversation-keys';

/**
 * Assign a new role to a conversation member
 */
export const useAssignMemberRole = (conversationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ targetUserId, role }: { targetUserId: string; role: ConversationRole }) =>
      assignMemberRole(conversationId, { targetUserId, role }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.permissions(conversationId)
      });
      queryClient.invalidateQueries({
        queryKey: conversationKeys.member(conversationId)
      });
      toast.success(i18next.t('conversations.rbac.roleAssigned'));
    },
    onError: () => {
      toast.error(i18next.t('conversations.rbac.roleAssignFailed'));
    }
  });
};

/**
 * Transfer conversation ownership to another member
 * Only current owner can perform this action
 */
export const useTransferOwnership = (conversationId: string) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (newOwnerId: string) => transferOwnership(conversationId, { newOwnerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.permissions(conversationId)
      });
      queryClient.invalidateQueries({
        queryKey: conversationKeys.member(conversationId)
      });
      toast.success(i18next.t('conversations.rbac.ownershipTransferred'));
    },
    onError: () => {
      toast.error(i18next.t('conversations.rbac.transferFailed'));
    }
  });

  return {
    ...mutation,
    transfer: async (newOwnerId: string) => {
      const confirmed = await confirmMes({
        title: i18next.t('conversations.rbac.transferOwnershipTitle'),
        description: i18next.t('conversations.rbac.transferOwnershipDesc')
      });
      if (confirmed) {
        return mutation.mutateAsync(newOwnerId);
      }
    }
  };
};

/**
 * Update permission overrides for a member
 * Requires ROLE_ASSIGN permission
 */
export const useUpdatePermissionOverrides = (conversationId: string, memberId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (permissions: PermissionOverrideItemDto[]) =>
      updateMemberOverrides(conversationId, memberId, { permissions }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.memberOverrides(conversationId, memberId)
      });
      toast.success(i18next.t('conversations.rbac.permissionsUpdated'));
    },
    onError: () => {
      toast.error(i18next.t('conversations.rbac.permissionUpdateFailed'));
    }
  });
};
